import { useMemo } from 'react';

import { type Picture } from '@/components/atoms';
import {
  DEFAULT_INTRO_SLIDES,
  INTRO_SLIDE_MAX_WIDTH,
  type IntroMediaName,
  introMedia,
} from '@/config/introVehicles';

export type PageIntroSlide = {
  name: IntroMediaName;
  image: Picture;
  kind: 'cutout' | 'photo';
  /** How wide this car may be drawn, in CSS pixels. */
  width: number;
  /** `sizes`, stated from that width so no phone downloads the desktop rendition. */
  sizes: string;
};

/**
 * The cars the band loops through, resolved from the names a page passes.
 *
 * Each width is taken from the photograph and not from the layout: the design
 * maximum or the source's own width, whichever is smaller. Sizing the other
 * way round is what blows a 589px cut-out across a desktop band, which no
 * amount of re-encoding could make sharp again.
 *
 * Memoised on the joined names rather than on `media`, because every page
 * passes that prop as a literal and a literal is a new object on every render.
 *
 * Nothing here marks the opening car as the priority fetch any more. It used
 * to, and the flag was right while the band was a stage at every width; below
 * `md` the band has no stage and the layer is not painted, and an eager image
 * is fetched even inside a hidden ancestor. See PageIntroSlideshow.
 */
export function usePageIntroSlides(
  enabled: boolean,
  slides?: readonly IntroMediaName[],
): PageIntroSlide[] | null {
  const names = enabled ? (slides ?? DEFAULT_INTRO_SLIDES) : null;
  const key = names?.join();

  return useMemo(() => {
    if (!key) return null;
    return key.split(',').map((name) => {
      const { image, kind } = introMedia[name as IntroMediaName];
      const width = Math.min(INTRO_SLIDE_MAX_WIDTH, image.img.w);
      const sizes =
        kind === 'photo'
          ? '(min-width: 900px) 320px, (min-width: 600px) 216px, 36vw'
          : '(min-width: 900px) 360px, 44vw';
      return { name: name as IntroMediaName, image, kind, width, sizes };
    });
  }, [key]);
}
